import { Box } from "@mui/material";
import React from "react";

interface AnchorPaginationProps {
  count: number;
  page: number;
  onChange: (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    value: number,
  ) => void;
  baseHref?: string;
}

const AnchorPagination: React.FC<AnchorPaginationProps> = ({
  count,
  page,
  onChange,
  baseHref = "",
}) => {
  const renderPageNumbers = () => {
    const pageNumbers = [];
    // Add first page button
    pageNumbers.push(
      <a
        key="first"
        href={`${baseHref}?page=1`}
        onClick={(e) => {
          e.preventDefault();
          if (page !== 1) onChange(e, 1);
        }}
        style={{
          margin: "0 4px",
          padding: "8px 12px",
          textDecoration: "none",
          color: page === 1 ? "#bdbdbd" : "#1976d2",
          fontWeight: "medium",
          cursor: page === 1 ? "default" : "pointer",
        }}
      >
        «
      </a>,
    );
    // Previous page
    pageNumbers.push(
      <a
        key="prev"
        href={`${baseHref}?page=${Math.max(1, page - 1)}`}
        onClick={(e) => {
          e.preventDefault();
          if (page > 1) onChange(e, page - 1);
        }}
        style={{
          margin: "0 4px",
          padding: "8px 12px",
          textDecoration: "none",
          color: page === 1 ? "#bdbdbd" : "#1976d2",
          fontWeight: "medium",
          cursor: page === 1 ? "default" : "pointer",
        }}
      >
        ‹
      </a>,
    );

    // Always show first page number
    pageNumbers.push(
      <a
        key={1}
        href={`${baseHref}?page=1`}
        onClick={(e) => {
          e.preventDefault();
          if (page !== 1) onChange(e, 1);
        }}
        style={{
          margin: "0 4px",
          padding: "8px 12px",
          textDecoration: "none",
          color: page === 1 ? "rgba(0, 0, 0, 0.87)" : "black",
          backgroundColor: page === 1 ? "rgba(0, 0, 0, 0.08)" : "transparent",
          borderRadius: "4px",
          fontWeight: "medium",
          cursor: page === 1 ? "default" : "pointer",
        }}
      >
        1
      </a>,
    );

    // Calculate range of page numbers to show
    let startPage = Math.max(2, page - 2);
    let endPage = Math.min(count - 1, page + 2);
    if (page <= 3) {
      startPage = 2;
      endPage = Math.min(5, count - 1);
    } else if (page >= count - 2) {
      startPage = Math.max(2, count - 4);
      endPage = count - 1;
    }

    // Ellipsis after first page if needed
    if (startPage > 2) {
      pageNumbers.push(
        <span key="start-ellipsis" style={{ margin: "0 4px" }}>
          ...
        </span>,
      );
    }

    // Add page numbers in the window
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <a
          key={i}
          href={`${baseHref}?page=${i}`}
          onClick={(e) => {
            e.preventDefault();
            if (page !== i) onChange(e, i);
          }}
          style={{
            margin: "0 4px",
            padding: "8px 12px",
            textDecoration: "none",
            color: page === i ? "rgba(0, 0, 0, 0.87)" : "black",
            backgroundColor: page === i ? "rgba(0, 0, 0, 0.08)" : "transparent",
            borderRadius: "4px",
            fontWeight: "medium",
            cursor: page === i ? "default" : "pointer",
          }}
        >
          {i}
        </a>,
      );
    }

    // Ellipsis before last page if needed
    if (endPage < count - 1) {
      pageNumbers.push(
        <span key="end-ellipsis" style={{ margin: "0 4px" }}>
          ...
        </span>,
      );
    }

    // Always show last page number if more than 1 page
    if (count > 1) {
      pageNumbers.push(
        <a
          key={count}
          href={`${baseHref}?page=${count}`}
          onClick={(e) => {
            e.preventDefault();
            if (page !== count) onChange(e, count);
          }}
          style={{
            margin: "0 4px",
            padding: "8px 12px",
            textDecoration: "none",
            color: page === count ? "rgba(0, 0, 0, 0.87)" : "black",
            backgroundColor: page === count ? "rgba(0, 0, 0, 0.08)" : "transparent",
            borderRadius: "4px",
            fontWeight: "medium",
            cursor: page === count ? "default" : "pointer",
          }}
        >
          {count}
        </a>,
      );
    }

    // Next page
    pageNumbers.push(
      <a
        key="next"
        href={`${baseHref}?page=${Math.min(count, page + 1)}`}
        onClick={(e) => {
          e.preventDefault();
          if (page < count) onChange(e, page + 1);
        }}
        style={{
          margin: "0 4px",
          padding: "8px 12px",
          textDecoration: "none",
          color: page === count ? "#bdbdbd" : "#1976d2",
          fontWeight: "medium",
          cursor: page === count ? "default" : "pointer",
        }}
      >
        ›
      </a>,
    );
    // Add last page button
    pageNumbers.push(
      <a
        key="last"
        href={`${baseHref}?page=${count}`}
        onClick={(e) => {
          e.preventDefault();
          if (page !== count) onChange(e, count);
        }}
        style={{
          margin: "0 4px",
          padding: "8px 12px",
          textDecoration: "none",
          color: page === count ? "#bdbdbd" : "#1976d2",
          fontWeight: "medium",
          cursor: page === count ? "default" : "pointer",
        }}
      >
        »
      </a>,
    );
    return pageNumbers;
  };
  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
      {renderPageNumbers()}
    </Box>
  );
};

export default AnchorPagination;
